<template>
  <v-dialog
      max-width="650px"
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="save"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Search</span>
      </v-card-title>
      <v-card-text class="resizable-card__body">
        <v-row dense>
          <v-col cols="12" sm="4" md="4">
            <r-search-kind :model.sync="kind" label="Type:"/>
          </v-col>
        </v-row>
        <v-row v-if="kind.id === 1" dense>
          <v-col cols="12" sm="12" md="12">
            <r-item-table :items.sync="model.items" title="Items"/>
          </v-col>
          <v-col cols="12" sm="12" md="12">
            <v-divider/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6" v-if="model.items.length>0">
            <r-search-filter :model.sync="filter" label="Filter:"/>
          </v-col>
        </v-row>
        <v-row dense v-if="model.kind && model.kind.id && model.kind.id === 1">
          <v-col>
            <v-checkbox label="Search Lowest Level" v-model="model.searchLowestLevel" class="mt-3" hide-details/>
          </v-col>

          <v-col>
            <v-checkbox label="Exclude Sourcing" v-model="model.excludeSourcing" class="mt-3" hide-details/>
          </v-col>

          <v-col>
            <v-checkbox label="Take Nearest" v-model="model.takeNearest" class="mt-3" hide-details/>
          </v-col>
        </v-row>
        <v-row v-if="kind.id === 1" dense>
          <v-col cols="12" md="4" sm="4">
            <r-point label="Arrow Offset" :model.sync="model.offset" hide-details/>
          </v-col>
          <v-col cols="12" sm="8" md="8">
            <r-tutorial-dropdown :model.sync="model.tutorial" :clearable="true"/>
          </v-col>
        </v-row>
        <v-row v-if="kind.id === 3" dense>
          <v-col cols="12" md="4" sm="4">
            <r-tile label="Tile" :model.sync="model.tile" hide-details/>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>Cancel</v-btn>
        <v-btn @click="save" color="blue darken-1" text>Save</v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown="startResize"></div>
    </v-card>
  </v-dialog>
</template>

<script>
import {ViewRules} from "@/common/ViewRules";
import {SearchKind} from "@/modules/redactor/models/search/SearchKind";
import {SearchModel} from "@/modules/redactor/models/search/SearchModel";
import {SearchFilter} from "@/modules/redactor/models/search/SearchFilter";

import RSearchKind from "@/modules/redactor/views/search/r-search-kind";
import RSearchFilter from "@/modules/redactor/views/search/r-search-filter";
import RPoint from "@/modules/redactor/views/utils/r-point";
import RItemTable from "@/modules/redactor/views/item/r-item-table";
import RTutorialDropdown from "@/modules/redactor/views/tutorial/r-tutorial-dropdown";
import RTile from "@/modules/redactor/views/utils/r-tile.vue";


export default {
  name: "r-search-dialog",
  components: {RTile, RTutorialDropdown, RItemTable, RPoint, RSearchFilter, RSearchKind},
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: new SearchModel(),
    rules: ViewRules.READONLY,
    panels: [],
    dialogWidth: 747,
    dialogHeight: 546,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 747,
    resizeStartHeight: 546,
    viewportPadding: 32
  }),
  computed: {
    kind: {
      get: function () {
        return this.model.kind ? this.model.kind : SearchKind.NONE;
      },
      set: function (value) {
        this.model.kind = value;
      }
    },
    filter: {
      get: function () {
        return this.model.filter ? this.model.filter : SearchFilter.NONE;
      },
      set: function (value) {
        this.model.filter = value;
      }
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new SearchModel();
      this.panels = [];
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.empty()) {
        this.resolve(null);
        this.discard();
      } else if (this.model.valid()) {
        this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 400;
      const minHeight = 260;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
}
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>